<template lang="pug">
.system-setting-page.flex.column.gap-20
  .flex.column.gap-6
    .desc 系統雜項設定，僅管理員可檢視與修改。
    .desc(v-if="updateText") {{ updateText }}

  .section.flex.column.gap-10
    .title.bold 專業人士不可排案時段通知
    .desc 專業人士在專業人士平台新增或移除今天以後的不可排案時段時，寄送通知給下列收件者（管理員以檢視視角操作時不通知）。
    .row.flex.align-center.gap-10
      .label 通知類型
      el-select.field(v-model="notifyType", :disabled="!loaded")
        el-option(
          v-for="opt in notifyTypeOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value")
    .row.flex.align-start.gap-10(v-if="notifyType === 'email'")
      .label.email-label 收件 Email
      .flex.column.gap-6.field
        el-input(
          v-model="notifyEmails"
          type="textarea"
          :autosize="{ minRows: 2, maxRows: 6 }"
          :disabled="!loaded"
          placeholder="多個 Email 以逗號分隔，例如 a@example.com, b@example.com")
        .flex.align-center.gap-6.wrap(v-if="parsedEmails.length > 0")
          el-tag(
            v-for="item in parsedEmails"
            :key="item.email"
            size="small"
            :type="item.valid ? '' : 'danger'") {{ item.email }}
        .desc(v-else) 未填寫時不寄送通知
        .desc.danger-color(v-if="invalidEmails.length > 0") Email 格式錯誤：{{ invalidEmails.join('、') }}

  .section.flex.column.gap-10
    .title.bold 個案紀錄設定
    .desc 關閉後，進階個管師只能查看「預約所屬專案」中有設定為可查看的個案紀錄內容，其他預約（含未選專案的預約）只會看到已完成。請先在「專案設定」設定好各專案的可查看清單再關閉。
    .row.flex.align-center.gap-10
      el-checkbox(v-model="recordAdvstaffViewAll", :disabled="!loaded") 所有進階個管師都可查看個案紀錄（不受專案清單限制）

  .op
    el-button(type="primary", :disabled="!loaded || invalidEmails.length > 0", @click="save") 儲存
</template>

<script>
import { getSystemSettings, updateSystemSettings } from '@/api/systemSetting';

const NOTIFY_TYPE_KEY = 'professional_unavailable_notify_type';
const NOTIFY_EMAILS_KEY = 'professional_unavailable_notify_emails';
const RECORD_ADVSTAFF_VIEW_ALL_KEY = 'record_advstaff_view_all';
// 與後端 Django validate_email 大致相同的寬鬆檢查，最終以後端驗證為準
const EMAIL_PATTERN = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;

/**
 * "a@x.com, b@y.com ,," → ["a@x.com", "b@y.com"]：逗號分隔、去前後空白、略過空白與重複
 * @param {string} value
 * @returns {string[]}
 */
function parseEmailList(value) {
  const emails = [];
  (value || '').split(',').forEach((item) => {
    const email = item.trim();
    if (email && !emails.includes(email)) {
      emails.push(email);
    }
  });
  return emails;
}

export default {
  data() {
    return {
      loaded: false,
      notifyType: 'email',
      notifyEmails: '',
      recordAdvstaffViewAll: true,
      updatedAt: null,
      updatedBy: null,
      notifyTypeOptions: [
        { value: 'email', label: 'Email' },
      ],
    };
  },
  computed: {
    parsedEmails() {
      return parseEmailList(this.notifyEmails).map((email) => ({ email, valid: EMAIL_PATTERN.test(email) }));
    },
    invalidEmails() {
      return this.parsedEmails.filter((item) => !item.valid).map((item) => item.email);
    },
    updateText() {
      if (!this.updatedAt) {
        return '';
      }
      const by = this.updatedBy ? `，由 ${this.updatedBy}` : '';
      return `最後更新：${this.$timestampToDateTimeMinute(this.updatedAt)}${by}`;
    },
  },
  methods: {
    applyData(data) {
      this.notifyType = data.settings[NOTIFY_TYPE_KEY];
      this.notifyEmails = data.settings[NOTIFY_EMAILS_KEY];
      this.recordAdvstaffViewAll = data.settings[RECORD_ADVSTAFF_VIEW_ALL_KEY] !== 'false';
      this.updatedAt = data.updated_at;
      this.updatedBy = data.updated_by;
      this.loaded = true;
    },
    loadData() {
      this.$execWithLoading(async () => {
        this.applyData(await getSystemSettings());
      }, (e) => {
        this.$showAxiosException('讀取系統設定失敗', e);
      });
    },
    save() {
      this.$execWithLoading(async () => {
        const data = await updateSystemSettings({
          [NOTIFY_TYPE_KEY]: this.notifyType,
          [NOTIFY_EMAILS_KEY]: this.notifyEmails,
          [RECORD_ADVSTAFF_VIEW_ALL_KEY]: this.recordAdvstaffViewAll ? 'true' : 'false',
        });
        this.applyData(data);
        this.$showSuccess('儲存成功');
      }, (e) => {
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 400 && e.response.data.key === NOTIFY_EMAILS_KEY && detail) {
          this.$showError(`Email 格式錯誤：${detail}`);
          return;
        }
        this.$showAxiosException('儲存失敗', e);
      });
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.system-setting-page {
  overflow-y: auto;
  height: 100%;
}
.desc {
  color: $text-secondary-color;
  font-size: 12px;
}
.section {
  padding-bottom: 20px;
  border-bottom: 1px solid $border-light-color;
}
.label {
  flex: 0 0 80px;
}
.email-label {
  line-height: 32px;
}
.field {
  flex: 0 1 480px;
}
</style>
